import * as React from "react";
import { create } from "zustand";
import { createApi } from "../../../utils/createApi";
import {
  ApiCameraLiveness,
  CameraLiveness,
  mapCameraLiveness,
} from "./SiteAboutPageUtils/videoFlowStatus";

// One camera_liveness/ read per site (ENG-2068), shared by every camera row on
// the site page and by the Edit Camera row. Rows ask for it on first expand;
// concurrent asks share the in-flight request. A failure (including a 404 on
// an environment without ENG-2046) is remembered as "failed" and the rows keep
// today's display.

/** Re-read after this long so a page left open doesn't show old evidence. */
export const CAMERA_LIVENESS_TTL_MS = 2 * 60_000;

interface Entry {
  status: "loading" | "ready" | "failed";
  data: CameraLiveness | null;
  fetchedAt: number;
}

interface CameraLivenessState {
  bySite: Record<number, Entry>;
  ensureCameraLiveness: (siteId: number) => void;
}

export const useCameraLivenessStore = create<CameraLivenessState>(
  (set, get) => ({
    bySite: {},
    ensureCameraLiveness: (siteId: number) => {
      if (!siteId) return;
      const existing = get().bySite[siteId];
      const now = Date.now();
      if (
        existing &&
        (existing.status === "loading" ||
          now - existing.fetchedAt < CAMERA_LIVENESS_TTL_MS)
      ) {
        return;
      }
      const setEntry = (entry: Entry) =>
        set((state) => ({ bySite: { ...state.bySite, [siteId]: entry } }));
      setEntry({
        status: "loading",
        data: existing?.data ?? null,
        fetchedAt: now,
      });
      createApi("")
        .get<ApiCameraLiveness>(`customer/${siteId}/camera_liveness/`, {
          timeout: 30_000,
        })
        .then((response) =>
          setEntry({
            status: "ready",
            data: mapCameraLiveness(response.data),
            fetchedAt: Date.now(),
          })
        )
        .catch(() =>
          setEntry({ status: "failed", data: null, fetchedAt: Date.now() })
        );
    },
  })
);

/** The site's liveness payload, or null while loading / on failure. */
export const useCameraLiveness = (
  siteId: number | null | undefined
): CameraLiveness | null => {
  const ensure = useCameraLivenessStore((state) => state.ensureCameraLiveness);
  const entry = useCameraLivenessStore((state) =>
    siteId ? state.bySite[siteId] : undefined
  );
  React.useEffect(() => {
    if (siteId) ensure(siteId);
  }, [siteId, ensure]);
  return entry?.data && entry.data.siteId === siteId ? entry.data : null;
};
